import { loadOidcOverride } from './state.js'

const BUILD_ENV = import.meta.env || {}

export function cleanBaseUrl(raw) {
  return String(raw || '').trim().replace(/\/+$/, '')
}

export function isDevLikeHost(host) {
  const h = String(host || '').toLowerCase()
  if (!h) return false
  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) return true
  if (h.startsWith('admin-dev.') || h.startsWith('iam-dev.') || h.startsWith('iam-backend-dev.') || h.startsWith('iam-api-dev.')) return true
  if (h.endsWith('-dev.chamii.com')) return true
  return h.includes('-dev.')
}

export function isTestLikeHost(host) {
  const h = String(host || '').toLowerCase()
  if (!h) return false
  if (h.startsWith('admin-test.') || h.startsWith('iam-test.') || h.startsWith('iam-backend-test.') || h.startsWith('iam-api-test.')) return true
  return h.endsWith('-test.chamii.com') || h.includes('-test.')
}

function isDevLikeBaseUrl(baseUrl) {
  try {
    return isDevLikeHost(new URL(String(baseUrl || '')).hostname)
  } catch {
    return false
  }
}

function isTestLikeBaseUrl(baseUrl) {
  try {
    return isTestLikeHost(new URL(String(baseUrl || '')).hostname)
  } catch {
    return false
  }
}

function isDevLikeSite() {
  try {
    return isDevLikeHost(window.location.hostname)
  } catch {
    return false
  }
}

function isTestLikeSite() {
  try {
    return isTestLikeHost(window.location.hostname)
  } catch {
    return false
  }
}

function canonicalChamiiSiteConfig() {
  try {
    const host = String(window.location.hostname || '').toLowerCase()
    if (host === 'admin-dev.chamii.com') {
      return { baseUrl: 'https://iam-backend-dev.chamii.com', clientId: 'chamii-main-dev' }
    }
    if (host === 'admin-test.chamii.com') {
      return { baseUrl: 'https://iam-backend-test.chamii.com', clientId: 'chamii-main-test' }
    }
    if (host === 'admin.chamii.com') {
      return { baseUrl: 'https://iam-api.chamii.com', clientId: 'chamii-main' }
    }
    if (host === 'vibokey.chamii.com') {
      return { baseUrl: 'https://iam-api.chamii.com', clientId: 'vibokey' }
    }
  } catch {
    // Fall through to build-time configuration outside canonical Chamii hosts.
  }
  return null
}

function preferEnvironmentBaseUrl(rawBaseUrl) {
  const baseUrl = cleanBaseUrl(rawBaseUrl)
  if (!baseUrl) return baseUrl
  try {
    const u = new URL(baseUrl)
    const host = String(u.hostname || '').toLowerCase()
    const port = u.port ? `:${u.port}` : ''
    if (isTestLikeSite()) {
      if (['iam-backend.chamii.com', 'iam-api.chamii.com', 'iam-backend-dev.chamii.com', 'iam-api-dev.chamii.com'].includes(host)) return `https://iam-backend-test.chamii.com${port}`
      if (host === 'iam.chamii.com' || host === 'iam-dev.chamii.com') return `https://iam-test.chamii.com${port}`
      return baseUrl
    }
    if (isDevLikeSite()) {
      if (host === 'iam-backend.chamii.com' || host === 'iam-api.chamii.com') return `https://iam-backend-dev.chamii.com${port}`
      if (host === 'iam.chamii.com') return `https://iam-dev.chamii.com${port}`
    }
    return baseUrl
  } catch {
    return baseUrl
  }
}

export function environmentClientId(rawClientId, baseUrl) {
  const clientId = String(rawClientId || '').trim()
  if (!clientId) return clientId
  const test = isTestLikeBaseUrl(baseUrl) || isTestLikeSite()
  const dev = isDevLikeBaseUrl(baseUrl) || isDevLikeSite()
  if (['chamii', 'chamii-dev', 'chamii-main', 'chamii-main-dev'].includes(clientId)) {
    if (test) return 'chamii-main-test'
    return dev ? 'chamii-main-dev' : 'chamii-main'
  }
  if (test) {
    if (clientId.endsWith('-dev')) return `${clientId.slice(0, -4)}-test`
    return clientId.endsWith('-test') ? clientId : `${clientId}-test`
  }
  if (!dev) return clientId
  return clientId.endsWith('-dev') ? clientId : `${clientId}-dev`
}

function inferIamBaseUrl() {
  const raw = String(BUILD_ENV.VITE_IAM_LOGIN_BASE_URL || '').trim()
  if (!raw) return 'https://iam-api.chamii.com'
  try {
    return new URL(raw, window.location.origin).origin
  } catch {
    return 'https://iam-api.chamii.com'
  }
}

export function getOidcConfig() {
  const canonical = canonicalChamiiSiteConfig()
  if (canonical) return { provider: 'iam', ...canonical }

  const baseUrlFromEnv = preferEnvironmentBaseUrl(BUILD_ENV.VITE_OIDC_BASE_URL || BUILD_ENV.VITE_KEYCLOAK_BASE_URL || inferIamBaseUrl())
  const realm = String(BUILD_ENV.VITE_KEYCLOAK_REALM || '').trim()
  const configuredClientId = String(BUILD_ENV.VITE_OIDC_CLIENT_ID || BUILD_ENV.VITE_IAM_APP_KEY || '').trim()
  const override = loadOidcOverride()
  const baseUrl = cleanBaseUrl(preferEnvironmentBaseUrl(override?.baseUrl || baseUrlFromEnv))
  const candidateClientId = environmentClientId(override?.clientId || configuredClientId || 'chamii-main', baseUrl)
  if (!baseUrl || !candidateClientId) return null
  if (realm) return { provider: 'keycloak', baseUrl, realm, clientId: candidateClientId }
  return { provider: 'iam', baseUrl, clientId: candidateClientId }
}

export function oidcRedirectUri() {
  return new URL('/oidc/callback', window.location.origin).toString()
}

export function oidcPostLogoutRedirectUri() {
  return new URL('/logout/callback', window.location.origin).toString()
}
